import React, { useEffect, useState, useRef, useCallback } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Campanella live per trader/studio. Polling `/notifications/unread` ogni 15s
 * (e on-focus). Mostra badge + toast quando arrivano nuove approvazioni di
 * listino fornitore.
 *
 * - Cliccando la campanella: marca le notifiche come lette + naviga a /fornitori.
 * - Toast automatico al primo rilevamento (entro 15s dall'approvazione admin).
 * - Non renderizzato per gli admin (hanno AdminLiveBell separata).
 */
const POLL_MS = 15000;
const NOTIFIED_KEY = "ah_user_notified_ids";

function loadNotifiedIds() {
  try {
    const raw = sessionStorage.getItem(NOTIFIED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveNotifiedIds(setObj) {
  try {
    sessionStorage.setItem(NOTIFIED_KEY, JSON.stringify(Array.from(setObj)));
  } catch {
    // ignore
  }
}

export default function UserLiveBell({ user }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const notifiedRef = useRef(loadNotifiedIds());
  const firstFetchRef = useRef(true);

  const eligible = user && user.role !== "admin";

  const fetchOnce = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread");
      const arr = Array.isArray(data?.items) ? data.items : [];
      setItems(arr);
      setCount(arr.length);

      // Toast per ogni notifica NUOVA (non ancora notificata in questa sessione).
      // Saltiamo il toast al primissimo fetch dopo il login (evita lo spam su refresh).
      if (!firstFetchRef.current) {
        for (const it of arr) {
          const key = `${it.type}:${it.supplier_id}:${it.at}`;
          if (!notifiedRef.current.has(key)) {
            const isApproved = it.status === "answered";
            const brand = it.supplier_name || "Listino";
            if (isApproved) {
              toast.success(`✅ ${brand} · listino approvato`, {
                description: it.discount_chain
                  ? `Sconto riservato: ${it.discount_chain}`
                  : (it.discount_pct ? `Sconto riservato: ${it.discount_pct}%` : "Apri il fornitore per vedere le condizioni."),
                duration: 8000,
              });
            } else if (it.status === "rejected") {
              toast.warning(`${brand} · richiesta listino`, {
                description: "Non è stato possibile approvarla. Scrivici dal supporto.",
                duration: 8000,
              });
            }
            notifiedRef.current.add(key);
          }
        }
        saveNotifiedIds(notifiedRef.current);
      }
      firstFetchRef.current = false;
    } catch {
      // silenzioso
    }
  }, []);

  useEffect(() => {
    if (!eligible) return;
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    const onFocus = () => fetchOnce();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [eligible, fetchOnce]);

  const handleClick = useCallback(async () => {
    // Marca come letto sul server + reset state
    try {
      await api.post("/notifications/seen");
    } catch {
      // ignore
    }
    setCount(0);
    setItems([]);
    // Se c'era almeno una notifica, porta direttamente alla pagina listini
    if (items.length > 0) {
      navigate("/fornitori");
    }
  }, [items.length, navigate]);

  if (!eligible) return null;

  const hasUnread = count > 0;
  const tooltip = hasUnread
    ? `${count} ${count === 1 ? "nuova notifica" : "nuove notifiche"} · clicca per aprire`
    : "Nessuna nuova notifica";

  return (
    <button
      type="button"
      onClick={handleClick}
      className="relative inline-flex items-center justify-center w-9 h-9 rounded-sm hover:bg-cream2 transition-colors"
      title={tooltip}
      aria-label={tooltip}
      data-testid="user-live-bell"
      data-count={count}
    >
      <Bell size={16} className={hasUnread ? "text-rose-700" : "text-slate1"} />
      {hasUnread && (
        <span
          className="absolute -top-0.5 -right-0.5 bg-rose-600 text-cream text-[9px] font-medium rounded-full min-w-[16px] h-4 px-1 inline-flex items-center justify-center animate-pulse"
          data-testid="user-live-bell-count"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}
